import { useState, useCallback } from 'react';
import debounce from 'lodash.debounce';

import { fromMoneyCoreFormat, toMoneyCoreFormat } from '@spa/shared/utils/money';

const UPDATE_FILTERS_DELAY_MS = 300;
const SEARCHABLE_PRICE_LENGTH = 3;

const filterNonDigitChars = string => string.toString().replaceAll(/[^0-9]/g, '');
const isEmptyOrDigitsWithOptionalPlus = string => /^([0-9]+\+?)?$/g.test(string);

const endValueDisplayFormat = (value, max) => (toMoneyCoreFormat(value) == max ? `${value}+` : value);

export const usePriceRange = ([startCoreFormat, endCoreFormat], [min, max], onChange) => {
  const [displayedRange, setDisplayedRange] = useState(() => {
    const [start, end] = [startCoreFormat, endCoreFormat].map(fromMoneyCoreFormat);

    return [start, endValueDisplayFormat(end, max)];
  });

  const internalOnChange = ([start, end]) => {
    setDisplayedRange([start, endValueDisplayFormat(end, max)]);
    onChange([start, end]);
  };
  const debouncedOnChange = useCallback(debounce(internalOnChange, UPDATE_FILTERS_DELAY_MS), []);

  const onStartChange = start => {
    if (!isEmptyOrDigitsWithOptionalPlus(start)) {
      return;
    }

    const [newStart, newEnd] = [start, displayedRange[1]];
    setDisplayedRange([newStart, endValueDisplayFormat(newEnd, max)]);

    onRangeChange([newStart, newEnd]);
  };

  const onEndChange = end => {
    if (!isEmptyOrDigitsWithOptionalPlus(end)) {
      return;
    }

    const [newStart, newEnd] = [displayedRange[0], end];
    setDisplayedRange([newStart, endValueDisplayFormat(newEnd, max)]);
    onRangeChange([newStart, newEnd]);
  };

  const onRangeChange = ([start, end]) => {
    if (start.length < SEARCHABLE_PRICE_LENGTH || end.length < SEARCHABLE_PRICE_LENGTH) {
      debouncedOnChange.cancel();

      return;
    }

    debouncedOnChange([start, end].map(filterNonDigitChars));
  };

  return {
    displayedRange,
    setDisplayedRange: ([start, end]) => setDisplayedRange([start, endValueDisplayFormat(end, max)]),
    onStartChange,
    onEndChange
  };
};
